// Row ⇄ SQL value codec (shared by the SQLite adapters).
import { TABLES, type FieldDef, type TableName } from '@vero/domain';

export type SqlValue = string | number | null;

export function encodeRow(table: TableName, row: Record<string, unknown>): { cols: string[]; values: SqlValue[] } {
  const fields = TABLES[table] as Record<string, FieldDef>;
  const cols: string[] = [];
  const values: SqlValue[] = [];
  for (const [name, def] of Object.entries(fields)) {
    cols.push(name);
    const v = row[name];
    if (v === undefined || v === null) { values.push(null); continue; }
    switch (def.t) {
      case 'bool': values.push(v ? 1 : 0); break;
      case 'int': values.push(Math.trunc(Number(v))); break;
      case 'number': values.push(Number(v)); break;
      case 'json': values.push(JSON.stringify(v)); break;
      default: values.push(String(v));
    }
  }
  return { cols, values };
}

export function decodeRow(table: TableName, raw: Record<string, unknown>): Record<string, unknown> {
  const fields = TABLES[table] as Record<string, FieldDef>;
  const out: Record<string, unknown> = {};
  for (const [name, def] of Object.entries(fields)) {
    const v = raw[name];
    if (v === null || v === undefined) continue;
    switch (def.t) {
      case 'bool': out[name] = Number(v) === 1; break;
      case 'int': case 'number': out[name] = Number(v); break;
      case 'json': out[name] = typeof v === 'string' ? JSON.parse(v) : v; break;
      default: out[name] = String(v);
    }
  }
  return out;
}

/** drop undefined/null so memory, SQLite and IDB rows compare equal */
export function normalizeRow<T extends object>(row: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) if (v !== undefined && v !== null) out[k] = v;
  return out as T;
}
